import { DetailedBetResponseApi, DetailedBetResponseApiItem } from 'lib/api/detailedBetResponseApi';
import {
  DetailedBetResponseApi as DetailedBetResponseApiHappybet,
  DetailedBetResponseApiItem as DetailedBetResponseApiItemHappybet,
} from 'sites/happybet/lib/api/detailedBetResponseApi';
import { Icon, IconsEnum } from 'components/Icons';
import React, { useMemo, useRef } from 'react';
import { getAmount, gtag } from 'utility/functions';
import { getTypologyByString, trasformStateinHappybetLang } from 'utility/dashboard/functions';

import { BetEventTableRowMobile } from 'components/betEventTableRowMobile';
import { CashOutDetailsMobile } from 'components/cashOutDetailsMobile';
import { DatoCmsMyBetsPage } from 'lib/datoCms/queries/getMyBetsPage';
import { DetailedBetRow } from 'components/detailedBetRow';
import { MovimentiTipoScommessaEnum } from 'types/swagger';
import { MyBet } from 'types/dashboard/myBet';
import { NoResult } from 'components/noResult';
import { SkeletonArea } from 'components/skeletonArea';
import { StateField } from 'components/mieScommesseTableRow/stateField/StateField';
import { TableField } from 'components/mieScommesseTableRow/tableField/TableField';
import format from 'date-fns/format';
import { selectIsStickyHeader } from 'components/header/selectors';
import styles from './DetailedBetMobile.module.scss';
import { useBlockScroll } from 'hooks/useBlockScroll';
import { useGetLabelByKey } from 'hooks/useLingUI';
import { useReactToPrint } from 'react-to-print';
import { useTypedSelector } from 'lib/centralStore';
import useWindowSize from 'hooks/useWindowSize';

// import { MovimentiTipoScommessaEnum } from 'features/carrello/types';

export type DetailedBetMobileProps = {
  backFunction: () => void;
  printFunction: () => void;
  myBetPage?: DatoCmsMyBetsPage;
  items?: DetailedBetResponseApiItem[] | DetailedBetResponseApiItemHappybet[];
  detailedResponse: DetailedBetResponseApi | DetailedBetResponseApiHappybet;
  isFetching?: boolean;
  isCarrello?: boolean;
  selectedBet?: MyBet;
  openModal?: () => void;
  type: MovimentiTipoScommessaEnum;
};

export const DetailedBetMobile = ({
  backFunction,
  printFunction,
  myBetPage,
  items,
  detailedResponse,
  isFetching,
  isCarrello,
  selectedBet,
  openModal,
  type = MovimentiTipoScommessaEnum.Sport,
}: DetailedBetMobileProps) => {
  const headerIsSticky = useTypedSelector(selectIsStickyHeader);
  const labelCashOut = useGetLabelByKey('cash-out');
  const labelTitleDetailedBet = useGetLabelByKey('dettaglio-scommessa');
  const labelPal = useGetLabelByKey('pal');
  const labelBigliettoNonTrovato = useGetLabelByKey('biglietto-non-trovato');
  const labelSingola = useGetLabelByKey('singola')!;
  const labelMultipla = useGetLabelByKey('multipla')!;
  const labelSistema = useGetLabelByKey('sistema')!;
  const labelMyBetOpen = useGetLabelByKey('label-my-bet-open')!;
  const refTable = useRef<HTMLDivElement>(null);
  const { isMobile } = useWindowSize();

  useBlockScroll(isCarrello && isMobile ? true : false);

  const formatter = new Intl.NumberFormat('it-IT', {
    style: 'currency',
    currency: 'EUR',

    // These options are needed to round to whole numbers if that's what you want.
    //minimumFractionDigits: 0, // (this suffices for whole numbers, but will print 2500.10 as $2,500.1)
    maximumFractionDigits: 2, // (causes 2500.99 to be printed as $2,501)
  });

  const shareFunction = () => {
    gtag({ section: 'Scommesse', detail: 'Condividi', event: 'Click Condividi Scommessa' }, true);
    if (navigator.share) {
      navigator.share({ url: `/share-ticket?id=${selectedBet!.idBiglietto}` });
    } else {
      openModal && openModal();
    }
  };

  const labels = useMemo(() => {
    return {
      labelSingola,
      labelMultipla,
      labelSistema,
    };
  }, [labelMultipla, labelSistema, labelSingola]);

  const pageStyle = `{ size: 2.5in 4in }`;

  const print = useReactToPrint({
    pageStyle: pageStyle,
    content: () => refTable.current,
  });

  return (
    <div className={`${styles.container} ${isCarrello ? styles.scroll : ''}`}>
      <div
        className={styles.headerMobile}
        style={
          !isCarrello
            ? {
                top: headerIsSticky ? '6rem' : '0%',
                transition: headerIsSticky ? 'top ease-in .6s' : 'top ease-in-out .3s',
              }
            : {}
        }
      >
        <div className={styles.titleMobile}>
          <button onClick={backFunction}>
            <Icon iconId={IconsEnum.ARROW_BACK} color="var(--color-on-bg-primary)" />
          </button>
          <span>{myBetPage ? myBetPage.titleDetailPage : labelTitleDetailedBet}</span>
        </div>
        <div className={styles.buttonsMobile}>
          <button onClick={shareFunction}>
            <Icon iconId={IconsEnum.SHARE} color="var(--color-on-bg-primary)" />
          </button>
          <button onClick={print}>
            <Icon iconId={IconsEnum.PRINTER} color="var(--color-on-bg-primary)" />
          </button>
        </div>
      </div>
      <div ref={refTable}>
        {selectedBet && (
          <React.Fragment>
            <div className={styles.containerArea}>
              <div className={styles.row}>
                <TableField
                  className={'accent'}
                  label={type}
                  value={selectedBet?.tipologia ? getTypologyByString(selectedBet?.tipologia, labels) : ''}
                />
              </div>
              <div className={styles.status}>
                <StateField value={trasformStateinHappybetLang(labelMyBetOpen, selectedBet?.stato) ?? ''} />
              </div>
            </div>
            {!isCarrello && (
              <CashOutDetailsMobile
                myBetsPage={myBetPage}
                data={{
                  bet: formatter.format(getAmount(selectedBet.importo)),
                  potentialPayout: formatter.format(getAmount(selectedBet.vincita)),
                  liveQuote: getAmount(selectedBet.quotaTotale),
                  withCashOut: formatter.format(getAmount(selectedBet.importoCashout)),
                  cashOutButton: `${labelCashOut} ${formatter.format(getAmount(selectedBet.importoCashout))}`,
                  idBiglietto: selectedBet.idBiglietto,
                }}
                bet={selectedBet}
              />
            )}
          </React.Fragment>
        )}
        <div className={`${isCarrello && !isMobile ? styles.tableCarrello : styles.tableMobile}`}>
          {isFetching ? (
            <React.Fragment>
              <SkeletonArea className={styles.skeleton} height="13.3" />
              <SkeletonArea className={styles.skeleton} height="13.3" />
              <SkeletonArea className={styles.skeleton} height="13.3" />
            </React.Fragment>
          ) : items && items.length > 0 ? (
            items.map((item, index) => (
              <BetEventTableRowMobile
                key={index}
                betDetails={{
                  date: item.dataAvvenimento ? format(new Date(item.dataAvvenimento), 'dd/MM/yyyy HH:mm') : '',
                  match: item.descrizioneAvvenimento,
                  score: null,
                  pal: `${myBetPage ? myBetPage.labelPalinsesto : labelPal} ${item.palinsesto}`,
                  quota: getAmount(item.quotaEvento),
                  result: item.descrizioneEvento,
                  resultLabel: item.descrizioneTipoScommessa,
                  sport: item.descrizione,
                  status: item.stato,
                  descriptionStatus: item.statoLabel ?? item.stato,
                  descrizioneSport: item.descrizioneSport,
                  idBetradar: item.idBetradar!,
                  idDisciplina: item?.codiceDisciplina!,
                  verticale: selectedBet?.verticale,
                  siglaSport: item.siglaSport,
                  palinsesto: item.codiceProgramma,
                  numAvvenimento: item.numAvvenimento,
                  tipologia: selectedBet?.tipologia,
                  descrizioneCavalli: item.descrizioneCavalli,
                  fissa: item.fissa,
                }}
                index={index}
              />
            ))
          ) : (
            <NoResult noMargin label={myBetPage ? myBetPage.labelBigliettoNonTrovato : labelBigliettoNonTrovato} />
          )}
        </div>
        <DetailedBetRow bet={selectedBet} myBetPage={myBetPage} detailedResponse={detailedResponse!} />
      </div>
    </div>
  );
};
